import {create} from "zustand"
import {persist} from "zustand/middleware"
import axios, {AxiosResponse} from "axios"

interface Tokens {
	accessToken: string
	idToken: string
	refreshToken: string
	expiresIn: number
}

interface UserProfile {
	id: string
	user_ID: string
	email: string
	first_name?: string
	sur_name?: string
	phone?: string
	role?: UserRole
	status?: string
}

export type UserRole = "user" | "admin" | "developer" | "viewer" | "viewer_all"

// Roles allowed into the console. The backend enforces the same rule (403 for anything else).
const CONSOLE_ROLES: ReadonlyArray<UserRole> = ["admin", "developer", "viewer", "viewer_all"]
export const isConsoleRole = (role?: string): boolean =>
	!!role && (CONSOLE_ROLES as ReadonlyArray<string>).includes(role)

// Read-only roles: they may look (viewer: own devices, viewer_all: every device) but never write.
// The backend answers every write (shadow, firmware/RTL, job cancel, history edit) with 403.
const READ_ONLY_ROLES: ReadonlyArray<UserRole> = ["viewer", "viewer_all"]
export const isReadOnlyRole = (role?: string): boolean =>
	!!role && (READ_ONLY_ROLES as ReadonlyArray<string>).includes(role)
// Shown when a store refuses a write for a read-only role before sending the request.
export const READ_ONLY_REFUSED = "Your account has read-only access: this action is not available."

interface AuthState {
	status: "loading" | "authenticating" | "authenticated" | "unauthenticated" | "unauthorized"
	accessToken: string | null
	idToken: string | null
	refreshToken: string | null
	expiresAt: number | null
	currentSession: string | null
	isOTPPending: boolean
	userProfile: UserProfile | null
	sendOTP: (email: string) => Promise<void>
	verifyOTP: (email: string, code: string) => Promise<void>
	setTokens: (tokens: Tokens) => void
	setUserProfile: (profile: UserProfile) => void
	clearTokens: () => void
	logout: () => void
	resetOTPState: () => void
	initializeAuth: () => void
	checkTokenExpiry: () => boolean
	checkConsoleAccess: () => boolean
	isAdmin: () => boolean
	isDeveloper: () => boolean
	isReadOnly: () => boolean
}

interface SendOTPRequest {
	action: "sendOTP"
	email: string
}

interface VerifyOTPRequest {
	action: "verifyOTP"
	email: string
	code: string
	session: string
}

interface SendOTPResponse {
	message: string
	session: string
	challengeName: string
}

interface VerifyOTPResponse {
	success: true
	accessToken: string
	idToken: string
	refreshToken: string
	expiresIn: number
	userProfile: UserProfile
}

interface ErrorResponse {
	error: string
}

const API_BASE_URL = process.env.REACT_APP_LAMBDA_AUTH

const api = axios.create({
	baseURL: API_BASE_URL,
	headers: {
		"Content-Type": "application/json",
	},
	timeout: 10000,
})

export const useAuthStore = create<AuthState>()(
	persist(
		(set, get) => ({
			status: "loading",
			accessToken: null,
			idToken: null,
			refreshToken: null,
			expiresAt: null,
			currentSession: null,
			isOTPPending: false,
			userProfile: null,

			checkTokenExpiry: () => {
				const {expiresAt} = get()
				if (!expiresAt) return false

				const fiveMinutesFromNow = Date.now() + 5 * 60 * 1000
				return expiresAt > fiveMinutesFromNow
			},

			checkConsoleAccess: () => isConsoleRole(get().userProfile?.role),

			isAdmin: () => get().userProfile?.role === "admin",

			isDeveloper: () => get().userProfile?.role === "developer",

			isReadOnly: () => isReadOnlyRole(get().userProfile?.role),

			initializeAuth: () => {
				const {accessToken, checkTokenExpiry, checkConsoleAccess, clearTokens} = get()

				if (accessToken && checkTokenExpiry()) {
					if (checkConsoleAccess()) {
						set({status: "authenticated"})
					} else {
						clearTokens()
						set({status: "unauthorized"})
					}
				} else {
					clearTokens()
				}
			},

			setTokens: ({accessToken, idToken, refreshToken, expiresIn}) => {
				const expiresAt = Date.now() + expiresIn * 1000
				set({
					status: "authenticated",
					accessToken,
					idToken,
					refreshToken,
					expiresAt,
					currentSession: null,
					isOTPPending: false,
				})
			},

			setUserProfile: (profile: UserProfile) => {
				set({
					userProfile: profile,
				})
			},

			clearTokens: () =>
				set({
					status: "unauthenticated",
					accessToken: null,
					idToken: null,
					refreshToken: null,
					expiresAt: null,
					currentSession: null,
					isOTPPending: false,
					userProfile: null,
				}),

			logout: () => {
				set({
					status: "unauthenticated",
					accessToken: null,
					idToken: null,
					refreshToken: null,
					expiresAt: null,
					currentSession: null,
					isOTPPending: false,
					userProfile: null,
				})
			},

			resetOTPState: () => {
				set({
					currentSession: null,
					isOTPPending: false,
					status: "loading",
				})
			},

			sendOTP: async (email: string) => {
				const currentState = get()

				if (currentState.isOTPPending && currentState.currentSession) {
					return
				}

				const payload: SendOTPRequest = {
					action: "sendOTP",
					email,
				}

				set({status: "authenticating", isOTPPending: true})

				try {
					const response: AxiosResponse<SendOTPResponse> = await api.post("", payload)

					set({
						currentSession: response.data.session,
						status: "loading",
					})
				} catch (error) {
					console.error("SendOTP error:", error)
					set({
						status: "unauthenticated",
						currentSession: null,
						isOTPPending: false,
					})

					if (axios.isAxiosError(error)) {
						if (error.response) {
							const errorData = error.response.data as ErrorResponse
							throw new Error(errorData.error || `Server error: ${error.response.status}`)
						} else if (error.request) {
							console.error("No response received:", error.request)
							throw new Error(
								"No response from server - check network connection or CORS configuration",
							)
						} else {
							console.error("Request setup error:", error.message)
							throw new Error("Request configuration error")
						}
					}
					throw new Error("Unknown error occurred while sending OTP")
				}
			},

			verifyOTP: async (email: string, code: string) => {
				const currentSession = get().currentSession

				if (!currentSession) {
					throw new Error("No active session. Please request OTP first.")
				}

				const payload: VerifyOTPRequest = {
					action: "verifyOTP",
					email,
					code,
					session: currentSession,
				}

				set({status: "authenticating"})

				try {
					const response: AxiosResponse<VerifyOTPResponse> = await api.post("", payload)

					const data = response.data as VerifyOTPResponse

					if (data.success) {
						if (!isConsoleRole(data.userProfile.role)) {
							set({
								status: "unauthorized",
								currentSession: null,
								isOTPPending: false,
							})
							throw new Error("Access denied. This account has no console role.")
						}

						get().setTokens({
							accessToken: data.accessToken,
							idToken: data.idToken,
							refreshToken: data.refreshToken,
							expiresIn: data.expiresIn,
						})

						get().setUserProfile(data.userProfile)
					}
				} catch (error) {
					console.error("VerifyOTP error:", error)

					if (error instanceof Error && error.message.includes("Access denied")) {
						set({
							status: "unauthorized",
							currentSession: null,
							isOTPPending: false,
						})
						throw error
					}

					set({
						status: "unauthenticated",
						currentSession: null,
						isOTPPending: false,
					})

					if (axios.isAxiosError(error)) {
						if (error.response) {
							const errorData = error.response.data as ErrorResponse
							throw new Error(errorData.error || `Server error: ${error.response.status}`)
						} else if (error.request) {
							console.error("No response received:", error.request)
							throw new Error(
								"No response from server - check network connection or CORS configuration",
							)
						} else {
							console.error("Request setup error:", error.message)
							throw new Error("Request configuration error")
						}
					}
					throw new Error("Unknown error occurred while verifying OTP")
				}
			},
		}),
		{
			name: "auth-storage",
			partialize: (state) => ({
				accessToken: state.accessToken,
				idToken: state.idToken,
				refreshToken: state.refreshToken,
				expiresAt: state.expiresAt,
				userProfile: state.userProfile,
			}),
		},
	),
)

// Reactive selectors for components (re-render when the profile changes).
export const useUserRole = () => useAuthStore((state) => state.userProfile?.role)
export const useIsAdmin = () => useAuthStore((state) => state.userProfile?.role === "admin")
export const useIsDeveloper = () => useAuthStore((state) => state.userProfile?.role === "developer")
export const useIsReadOnly = () => useAuthStore((state) => isReadOnlyRole(state.userProfile?.role))

// Calls cb whenever the logged-in console user changes (logout, session expiry, or a different
// user logging in on the same browser). Stores use it to drop per-user data, incl. persisted data.
export const onConsoleUserChange = (cb: () => void) =>
	useAuthStore.subscribe((state, prev) => {
		if ((state.userProfile?.user_ID ?? null) !== (prev.userProfile?.user_ID ?? null)) cb()
	})
